import React, { useState, useEffect } from "react";
import {
	useParams,
	Link,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import {
	Form,
	Input,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Switch,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	InnerBlocks,
} from "./styles";
import LoadingSkeleton from "./components/loading-skeleton/LoadingSkeleton";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import { get_clients_name_list_agent_panel } from "../../../../../api/endpoints/clients";
import {
	get_agent_by_id,
	update_agent,
} from "../../../../../api/endpoints/agents";
import { CONSTANTS } from "../../../../../utils/constants";

const GUTTER = 10;

export default function UpdateAgent() {
	const { id } = useParams();
	const [form] = Form.useForm();
	const { userdata } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [initialValues, setInitialValues] = useState(null);
	const [clients, setClients] = useState([]);

	const webhookModeValue = Form.useWatch("webhookMode", form);

	useEffect(() => {
		const bootstrap = async () => {
			try {
				// 1) catálogos
				const clientsRes = await get_clients_name_list_agent_panel();
				setClients(
					(clientsRes ?? []).map((r) => ({ value: r.id, label: r.name })),
				);

				// 2) detalle
				if (!id) return;

				const data = await get_agent_by_id(id);

				const values = {
					clientId: data.clientId,
					phoneNumber: data.phoneNumber ?? "",
					phoneNumberId: data.phoneNumberId ?? "",
					isSeller: !!data.isSeller,
					isAssistant: !!data.isAssistant,
					isMultiClient: !!data.isMultiClient,
					webhookMode: data.webhookMode ?? "Prod",
					devProxyUrl: data.devProxyUrl ?? "",
				};

				setInitialValues(values);
				form.setFieldsValue(values);
			} catch (e) {
				console.error(e);
				openNotificationWithIcon(
					TYPE.ERROR,
					"No se pudo cargar el agente",
					"Verifique el id o contacte con soporte.",
				);
				navigate(`/admin/agentPanel?uri=${uri}`);
			}
		};

		bootstrap();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const normalizePhone = (v) => {
		if (!v) return v;
		return v.replace(/[^\d+]/g, "");
	};

	if (!id) navigate("/admin/agentPanel?uri=" + uri);

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => navigate(`/admin/agentPanel?uri=${uri}`),
		});
	};

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const agentPayload = {
				laboratoryId: values.clientId,
				phoneNumber: normalizePhone(values.phoneNumber),
				phoneNumberId: values.phoneNumberId?.trim(),
				isSeller: !!values.isSeller,
				isAssistant: !!values.isAssistant,
				isMultiClient: !!values.isMultiClient,
				webhookMode: values.webhookMode === "Prod" ? null : values.webhookMode,
				devProxyUrl: values.devProxyUrl?.trim() || null,
			};

			const payload = {
				id,
				agentConfig: agentPayload,
			};

			await update_agent(id, payload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Agente actualizado con éxito",
				"El agente se actualizó correctamente.",
			);

			navigate(`/admin/agentPanel?uri=${uri}`);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al actualizar el agente",
				error?.response?.data?.title ||
					error?.message ||
					"Un error ocurrió al intentar actualizar datos.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={`/admin/agentPanel?uri=${uri}`}>Listado de Agentes</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Editar agente</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				{!initialValues ? (
					<LoadingSkeleton />
				) : (
					<Form
						form={form}
						name="agent-panel"
						onFinish={onFinish}
						autoComplete="off"
						requiredMark={false}
						layout="vertical"
						initialValues={initialValues}
					>
						<InnerBlocks>
							<InnerForm>
								<ProductData>
									<CardContainer>
										<Row gutter={GUTTER}>
											<Col span={24}>
												<Form.Item
													label={
														<label
															style={{ color: COLORS.Primary, fontWeight: 600 }}
														>
															Laboratorio
														</label>
													}
													name="clientId"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Select
														showSearch
														placeholder="Elegí un laboratorio"
														optionFilterProp="children"
														filterOption={(input, option) =>
															(option?.label ?? "")
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														options={clients}
														// disabled
													/>
												</Form.Item>
											</Col>
										</Row>
									</CardContainer>
								</ProductData>

								<PublishSettings>
									<Container>
										<Space
											direction="vertical"
											size="middle"
											style={{ display: "flex" }}
										>
											<Form.Item>
												<SignInTemplate.AntdButton
													type="Primary"
													color="white"
													bg="Primary"
													htmlType="submit"
													loading={submitting}
													block
												>
													Guardar Cambios
												</SignInTemplate.AntdButton>
											</Form.Item>

											<SignInTemplate.AntdButton
												type="Primary"
												onClick={handleBack}
												loading={submitting}
												block
											>
												<Typo type="danger" level={6}>
													Volver sin guardar
												</Typo>
											</SignInTemplate.AntdButton>
										</Space>
									</Container>
								</PublishSettings>
							</InnerForm>

							<CardContainer>
								<Card
									title={<Typo level={5}>Datos del agente</Typo>}
									bodyStyle={{ paddingTop: 16 }}
								>
									<Row gutter={GUTTER}>
										<Col xs={24} md={12} lg={8}>
											<Card size="small" type="inner" title="WhatsApp" bordered>
												<Form.Item
													label="Numero"
													name="phoneNumber"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
														{
															validator: (_, v) => {
																if (!v) return Promise.resolve();
																const cleaned = normalizePhone(v);
																const digits = cleaned.replace(/\D/g, "");
																if (digits.length < 8)
																	return Promise.reject(
																		new Error("Número inválido (muy corto)."),
																	);
																return Promise.resolve();
															},
														},
													]}
												>
													<Input placeholder="+54911xxxxxxx" />
												</Form.Item>

												<Form.Item
													label="Meta Id"
													name="phoneNumberId"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input placeholder="Meta Phone Number ID" />
												</Form.Item>
											</Card>
										</Col>

										<Col xs={24} md={12} lg={8}>
											<Card
												size="small"
												type="inner"
												title="Capacidades"
												bordered
											>
												<Form.Item
													label="Es Vendedor"
													name="isSeller"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>

												<Form.Item
													label="Es Asistente"
													name="isAssistant"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>

												<Form.Item
													label="Es MultiCliente"
													name="isMultiClient"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
											</Card>
										</Col>

										<Col xs={24} md={12} lg={8}>
											<Card size="small" type="inner" title="Ruteo" bordered>
												<Form.Item
													label="Modo Webhook"
													name="webhookMode"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
													tooltip="Controla cómo se procesan los mensajes para este Agente."
												>
													<Select
														options={CONSTANTS.WEBHOOK_MODE_OPTIONS}
														placeholder="Seleccioná un modo"
													/>
												</Form.Item>

												<Form.Item
													label="DevProxy URL"
													name="devProxyUrl"
													tooltip="URL pública de dev/local a donde prod forwardea el webhook."
													dependencies={["webhookMode"]}
													rules={[
														({ getFieldValue }) => ({
															validator(_, v) {
																if (getFieldValue("webhookMode") !== "DevProxy")
																	return Promise.resolve();
																if (!v || !v.trim())
																	return Promise.reject(
																		new Error("Ingresá la URL de DevProxy."),
																	);
																try {
																	new URL(v.trim());
																	return Promise.resolve();
																} catch {
																	return Promise.reject(
																		new Error("URL inválida."),
																	);
																}
															},
														}),
													]}
												>
													<Input
														placeholder="https://app-ot-api-dev-eus-001.azurewebsites.net/api/chat/WhatsAppWebhook"
														disabled={webhookModeValue !== "DevProxy"}
													/>
												</Form.Item>
											</Card>
										</Col>
									</Row>
								</Card>
							</CardContainer>
						</InnerBlocks>
					</Form>
				)}
			</Main.Body>
		</Main>
	);
}
